import { useState } from 'react';
import { Plus } from 'lucide-react';
import { FormDialog } from '@/components/dialog/FormDialog';
import { SelectField } from '@/components/form/SelectField';
import { useSubmit } from '@/hooks/useSubmit';
import { COMPANY_ROLE } from '@/models/references/references';
import type UserModel from '@/models/entities/UserModel';
import type { CompanyOption } from '@/stores/useCompanyStore';
import { CompanyRoleField } from './CompanyRoleField';
import { createMember } from './members';
import { TeamField } from './TeamField';

/**
 * Ajout d'un utilisateur à une société (POST /members), depuis sa fiche : le pendant d'« Ajouter un employé » (liste des
 * employés). companies : les sociétés que l'utilisateur connecté dirige et dont l'utilisateur n'est pas encore employé.
 */
export function AddToCompanyDialog({
  user,
  companies,
  onClose,
  onSaved,
}: {
  user: UserModel;
  companies: CompanyOption[];
  onClose: () => void;
  /** teamError : motif si l'ajout à l'équipe a été refusé (l'utilisateur est tout de même ajouté à la société) */
  onSaved: (companyName: string, teamError: string | null) => void;
}) {
  const [companyId, setCompanyId] = useState(companies[0]?.id ?? 0);
  const [companyRole, setCompanyRole] = useState<string>(COMPANY_ROLE.EMPLOYEE);
  const [teamId, setTeamId] = useState<number | null>(null);
  const { submit, submitting, error } = useSubmit();
  const company = companies.find((item) => item.id === companyId);

  const save = () =>
    submit(async () => {
      if (!company) return;
      const teamError = await createMember(user.id, company.id, companyRole, teamId);
      onSaved(company.name, teamError);
    });

  return (
    <FormDialog
      title={`Ajouter ${user.fullName} à une société`}
      description="Choisissez la société, son rôle dans la société et, au besoin, son équipe."
      submitLabel="Ajouter"
      submitIcon={Plus}
      canSubmit={!!company}
      submitting={submitting}
      error={error}
      onSubmit={save}
      onClose={onClose}
    >
      <SelectField
        id="member-company"
        label="Société"
        required
        value={companyId}
        onChange={(e) => {
          setCompanyId(Number(e.target.value));
          // Équipes de l'autre société
          setTeamId(null);
        }}
      >
        {companies.map((item) => (
          <option key={item.id} value={item.id}>
            {item.name}
          </option>
        ))}
      </SelectField>
      <CompanyRoleField id="member-role" value={companyRole} onChange={setCompanyRole} />
      <TeamField id="member-team" companyId={company?.id ?? null} value={teamId} onChange={setTeamId} />
    </FormDialog>
  );
}
